import Cursor, { CursorTypes } from 'editor/components/Cursor';
import EditorMode, { EditorModes } from 'editor/components/EditorMode';
import MoveBoxCPInteraction from 'editor/components/interactions/MoveBoxCPInteraction';
import MoveLineCPInteraction from 'editor/components/interactions/MoveLineCPInteraction';
import MoveMarkupInteraction from 'editor/components/interactions/MoveMarkupInteraction';
import PanSheetInteraction from 'editor/components/interactions/PanSheetInteraction';
import Markup from 'editor/components/Markup';
import RFIPost from 'editor/components/RFIPost';
import UpdateZoom from 'editor/components/UpdateZoom';
import {
  MouseDownArgs,
  MouseMoveArgs,
  MouseUpArgs,
} from 'editor/systems/addSheetEvents/sheetEventHandlers';
import {
  BoxControlPoint,
  boxControlPointHit,
  getBoxControlPointCursor,
  lineControlPointHit,
} from 'editor/utils/controlPoints';
import { getHitMarkups } from 'editor/utils/hitDetection';

//Used to store the distance between the event spots on the page
let previousDist = -1;
const events: PointerEvent[] = [];

//Removes the event from the events array when
//the user releases one or both fingers
const removeEvent = (evt: PointerEvent) => {
  for (let i = 0; i < events.length; i++) {
    if (evt.pointerId === events[i].pointerId) {
      events.splice(i, 1);
      break;
    }
  }
};

const cursorEvents = {
  mouseDown(args: MouseDownArgs): void {
    const { evt, world } = args;
    events.push(evt);
    world.createEntity().add(new PanSheetInteraction());
  },

  mouseMove(args: MouseMoveArgs): void {
    const { carnDoc, evt, world, hit, markupKit } = args;
    const { MKPoint } = markupKit;

    //Update the pair of events in order to calculate pinch zoom distance
    for (let i = 0; i < events.length; i++) {
      if (evt.pointerId === events[i].pointerId) {
        events[i] = evt;
        break;
      }
    }

    const cursorEntity = world.locate(Cursor);

    // This shouldn't ever happen...
    if (!cursorEntity) {
      return;
    }

    const cursorComp = cursorEntity.get(Cursor);

    // Handle moving markups
    const mmi = world.locate(MoveMarkupInteraction);

    // Handle hovering and moving of control points
    const mcp = world.locate(MoveBoxCPInteraction); // box control points

    const mcp2 = world.locate(MoveLineCPInteraction); // line control points

    // Move and resize cursor will have already been set before the interaction existed,
    // so short circuiting here.
    if (mmi || mcp || mcp2) {
      return;
    }

    //Handles pinch zoom event
    if (events.length === 2) {
      carnDoc.doc.disablePan();
      const currentDist = Math.hypot(
        events[0].pageX - events[1].pageX,
        events[0].pageY - events[1].pageY
      );

      if (previousDist > 0) {
        if (currentDist > previousDist) {
          world.createEntity().add(carnDoc).add(new UpdateZoom('pinchZoomIn'));
        }
        if (currentDist < previousDist) {
          world.createEntity().add(carnDoc).add(new UpdateZoom('pinchZoomOut'));
        }
      }

      previousDist = currentDist;
      return;
    }

    // Handle standard grab/grabbing hand for panning of sheet.
    const psi = world.locate(PanSheetInteraction);

    if (psi) {
      cursorComp.cursor = CursorTypes.Grabbing;
      return;
    }

    const selectedMarkup = world
      .grabAll(Markup)
      .find(({ component: mc }) => mc.selected === true);

    // show move cursor if over a markup.
    const point = new MKPoint(hit.x, hit.y);
    const hitMarkups = getHitMarkups(world, point);

    if (hitMarkups.length > 0) {
      // Can't move punches at this time.
      const overPunch = hitMarkups.some(
        (entity) => entity.get<Markup>(Markup).isPunch
      );

      const overRFI = hitMarkups.some(
        (entity) => entity.get<Markup>(Markup).isRFI
      );

      if (overRFI) {
        cursorComp.cursor = CursorTypes.Pointer;

        const hasRFIPost = world.locate(RFIPost);

        if (hasRFIPost) {
          cursorComp.cursor = CursorTypes.Move; // move...
        }
      } else if (overPunch) {
        cursorComp.cursor = CursorTypes.Pointer;

        // Is the selected markup one of the hit markups?
        const selectedIsHit = hitMarkups.some(
          (ent) =>
            ent.get(Markup).markup.markupid ===
            selectedMarkup?.component.markup.markupid
        );

        if (selectedMarkup?.component.isPunch && selectedIsHit) {
          cursorComp.cursor = CursorTypes.Move;
        }
      } else if (!overPunch && EditorModes.Normal) {
        cursorComp.cursor = CursorTypes.Move;
      }

      return;
    }

    // Everything else in the function only has to do with non-viewonly mode.
    const modeComp = world.grab(EditorMode)?.component;

    if (!modeComp || modeComp?.mode === EditorModes.ViewOnly) {
      return;
    }

    if (selectedMarkup) {
      const { component: mc } = selectedMarkup;
      const markupType = mc.mkMarkup?.getType();

      switch (markupType) {
        case 'Square':
          {
            const cph = boxControlPointHit(mc, hit);

            if (cph !== BoxControlPoint.None) {
              cursorComp.cursor = getBoxControlPointCursor(cph);

              return;
            }
          }
          break;
        case 'FreeText':
          {
            const cph = boxControlPointHit(mc, hit);

            if (cph !== BoxControlPoint.None) {
              cursorComp.cursor = getBoxControlPointCursor(cph);

              return;
            }
          }
          break;
        case 'Line':
          {
            const cph = lineControlPointHit(mc, hit);

            if (cph !== null) {
              cursorComp.cursor = CursorTypes.Pointer;

              return;
            }
          }
          break;
        default:
      }
    }

    cursorComp.cursor = CursorTypes.Grab;
  },

  mouseUp(args: MouseUpArgs): void {
    const { carnDoc, evt, world } = args;

    removeEvent(evt);

    if (events.length < 2) {
      carnDoc.doc.enablePan();
      previousDist = -1;
    }

    world.locateAll(PanSheetInteraction).forEach((entity) => {
      world.grabAll(Cursor).forEach(({ component }) => {
        component.cursor = CursorTypes.Grab;
      });

      entity.destroy();
    });
  },
};

export default cursorEvents;
